import { useState } from "react";

import { useForm } from "react-hook-form";
import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";

import useEbay from "../../data/useEbay";
import useOfferUpdate from "../../data/useOfferUpdate";
import Modal from "../../layouts/Modal";
import { IPart } from "../../models/Part";
import useNotification from "../notifications/useNotifications";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SelectInput from "../shared/Inputs/SelectInput";

interface IUpdatePolicies {
  locationKey?: string;
  paymentPolicyId?: string;
  fulfillmentPolicyId?: string;
  returnPolicyId?: string;
}

function PoliciesForm({ part, setOpen }: { part: IPart; setOpen: (open: boolean) => void }) {
  const { policies, locations } = useEbay();
  const { updatePolicies } = useOfferUpdate();
  const { addNotification } = useNotification();
  const queryClient = useQueryClient();
  const offer = part.offers?.[0];

  const { register, setValue, watch, handleSubmit } = useForm<IUpdatePolicies>({
    defaultValues: {
      locationKey: offer?.locationKey ?? undefined,
      fulfillmentPolicyId: offer?.fulfillmentPolicyId ?? undefined,
      paymentPolicyId: offer?.paymentPolicyId ?? undefined,
      returnPolicyId: offer?.returnPolicyId ?? undefined,
    },
  });

  // Queued rather than a direct offer PUT: the worker rebuilds the update from the current
  // offer (so nothing outside these four fields is touched, unlike sending back the slim
  // table-row projection), retries transient eBay errors, and reports progress on the row's
  // "eBay Update" status cell.
  const onSubmit = (data: IUpdatePolicies) => {
    if (!offer) return;

    updatePolicies.mutate(
      {
        offerIds: [offer.id],
        locationKey: data.locationKey || undefined,
        fulfillmentPolicyId: data.fulfillmentPolicyId || undefined,
        paymentPolicyId: data.paymentPolicyId || undefined,
        returnPolicyId: data.returnPolicyId || undefined,
      },
      {
        onSuccess: (res) => {
          if (!res?.data?.queued) {
            addNotification({
              primaryText: res?.message || "Listing could not be queued for a policy update",
              variant: "error",
            });
            return;
          }

          addNotification({
            primaryText: "Policy update queued — progress shows on the listing",
            variant: "success",
          });
          queryClient.invalidateQueries(["pagedTable"]);
          setOpen(false);
        },
        //Surface the API/eBay reason instead of a generic failure - without it a rejected
        //policy change is indistinguishable from the update silently not saving.
        onError: (err: any) =>
          addNotification({
            primaryText: err?.message || "Failed to queue the policy update",
            variant: "error",
          }),
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <h2 className="text-lg font-medium leading-6 text-gray-900">Update eBay Listing Policies</h2>
        <p className="text-sm text-gray-600">
          Update the policies for this listing. Leave a field unchanged to keep its current value.
          The change is applied to eBay in the background — progress shows on the listing.
        </p>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <SelectInput
            label={"Location of Part"}
            register={register}
            registerName={"locationKey"}
            options={locations.data?.data.locations || []}
            setValue={setValue}
            watch={watch}
          />

          <SelectInput
            label={"Shipping Policy"}
            register={register}
            registerName={"fulfillmentPolicyId"}
            options={policies.data?.data.fulfilmentPolicies || []}
            setValue={setValue}
            watch={watch}
          />

          <SelectInput
            label={"Payment Policy"}
            register={register}
            registerName={"paymentPolicyId"}
            options={policies.data?.data.paymentPolicies || []}
            setValue={setValue}
            watch={watch}
          />

          <SelectInput
            label={"Returns Policy"}
            register={register}
            registerName={"returnPolicyId"}
            options={policies.data?.data.returnPolicies || []}
            setValue={setValue}
            watch={watch}
          />
        </div>

        <div className="flex justify-end gap-4 border-t pt-4">
          <ButtonInput
            label={"Cancel"}
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"}
          />
          <ButtonInput
            label={"Update Policies"}
            isSubmit={true}
            onClick={undefined}
            classes={undefined}
            isLoading={updatePolicies.isLoading}
          />
        </div>
      </div>
    </form>
  );
}

export default function ListingPoliciesButton({ part }: { part: IPart }) {
  const [open, setOpen] = useState(false);

  if (!part.offers?.[0]) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Update listing policies"
        className="flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 font-medium text-slate-600 transition hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
      >
        <ShieldCheckIcon className="h-4 w-4 shrink-0" />
        Update policies
      </button>

      <Modal open={open} setOpen={setOpen} width="max-w-5xl">
        {open && <PoliciesForm part={part} setOpen={setOpen} />}
      </Modal>
    </>
  );
}
